import { Button } from "primereact/button";
import {
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import "./TopBar.scss";
import {
  useDeployStreamMutation,
  useDeleteStreamMutation,
  useStopStreamMutation,
  useResumeStreamMutation,
  useLazyGetLastFlowStatusQuery,
  RuntimeMode,
  ITopology,
  useLazyGetTopologyQuery
} from "../../../app/apiSlice";
import { useSecureApplication } from "../../../hooks/KeycloackConnection";
import { setToast } from "../../../app/appSlice";
import { Dialog } from "primereact/dialog";
import {
  TopologyConfigurationError,
  useTopologyConfigurationValidator,
} from "../../../hooks/useTopologyConfigurationValidator";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { PipelineState, setPipelineState, setStreamNameAndRunID } from "../editorSlice";
import { useRedirect404 } from "../../../hooks/useRedirect404";
import { ConfirmDialog } from 'primereact/confirmdialog';
import { confirmDialog } from 'primereact/confirmdialog';


export const StartStopStream = (props: PropsWithChildren<{ topology: ITopology; onlyStop?: boolean }>) => {
  const { t } = useTranslation();

  const { hasAnyPerm } = useSecureApplication();
  const { validateTopologyConfiguration } = useTopologyConfigurationValidator();
  const [deployStream, deployStatus] = useDeployStreamMutation();
  const [stopStream, stopStatus] = useStopStreamMutation();
  const [resumeStream, resumeStatus] = useResumeStreamMutation();
  const [deleteStream, deleteStatus] = useDeleteStreamMutation();
  const [getLastFlowStatus, { data: lastflowStatus }] = useLazyGetLastFlowStatusQuery();
  const [getTopology] = useLazyGetTopologyQuery();

  const dispatch = useAppDispatch();

  const debugMode = useAppSelector((state) => state.editor.debugMode);
  const pipelineSaved = useAppSelector((state) => state.editor.changedTopology);
  const forcedFlowName = useAppSelector((state) => state.editor.current?.forcedflowname);

  const [pipeState, setPipeState] = useState(PipelineState.UNDEFINED);
  const [configErrors, setConfigErrors] = useState<TopologyConfigurationError[]>([]);

  const pipeStateRef = useRef(pipeState);
  const startingTimeoutRef = useRef<number | null>(null);

  let flowStatusTimer: NodeJS.Timer;

  useRedirect404(deployStatus.error, "treatments");
  useRedirect404(stopStatus.error, "treatments");
  useRedirect404(resumeStatus.error, "treatments");
  useRedirect404(deleteStatus.error, "treatments");

// -------------------------------------------------------------------------------------------------
// HOOKS
// -------------------------------------------------------------------------------------------------

  useEffect(() => {

    // Get flow status
    getLastFlowStatus({topoid: props.topology.id, env: props.topology.environnement});

    // Renew call every 3s
    flowStatusTimer = setInterval(() => {
      getLastFlowStatus({topoid: props.topology.id, env: props.topology.environnement});
    }, 3000);

    // ----------------------------------
    return onDestroy;
    // ----------------------------------
  }, []);

  useEffect(() => {
    pipeStateRef.current = pipeState; // Keep ref updated with the latest isRunning
    dispatch(setPipelineState(pipeState));
  }, [pipeState]);

  useEffect(() => {
    switch(lastflowStatus?.status) {
      case 'Running':
        if (pipeState === PipelineState.STARTING) {
          dispatch(setToast({severity: "success", content: `Lancement effectué avec succès`, life: 5000}));
        }
        setPipeState(PipelineState.RUNNING);
        dispatch(
          setStreamNameAndRunID({name: lastflowStatus.execution.workflow_id , runID: lastflowStatus.execution.run_id, topologyId: props.topology.id})
        );
        break;
      case 'Completed':
        setPipeState(PipelineState.COMPLETED);
        break;
      case 'Failed':
        setPipeState(PipelineState.FAILED);
        break;
      case 'Stopped':
        if (pipeState === PipelineState.STOPPING) {
          dispatch(setToast({severity: "success", content: `Mise en pause effectuée avec succès`, life: 5000}));
        }
        setPipeState(PipelineState.STOPPED);
        break;
      case '':
      case 'Terminated':
        if (pipeState === PipelineState.DELETING) {
          dispatch(setToast({severity: "success", content: `Arrêt effectué avec succès`, life: 5000}));
        }
        setPipeState(PipelineState.DELETED);
        break;
      default:
        break;
    }
  }, [lastflowStatus]);

// -------------------------------------------------------------------------------------------------
// PRIVATE METHODS
// -------------------------------------------------------------------------------------------------

  const onDestroy = () => {
    clearInterval(flowStatusTimer);
    if (startingTimeoutRef.current !== null) {
      clearTimeout(startingTimeoutRef.current);
    }
  }

  // If a flow is not running for more than 60 seconds it stops automatically
  const setStartingTimeout = () => {

    // clear timerRef if a timer is alraedy launched
    cancelStartingTimeout();

    // wait one minute to stop the flow if is not running
    startingTimeoutRef.current = window.setTimeout(() => {
      // check if flow is starting with isRunningRef to have the state up-to-date
      if (pipeStateRef.current === PipelineState.STARTING) {
        setPipeState(PipelineState.STOPPED);
        dispatch(setToast({severity: "error", content: "Echec du lancement", life: 5000,}));
      }
    }, 60000);
  };

  const cancelStartingTimeout = () => {
    if (startingTimeoutRef.current) {
      window.clearTimeout(startingTimeoutRef.current);
      startingTimeoutRef.current = null;
    }
  }

  const verifyTopology = () => {
    const errors = validateTopologyConfiguration();
    setConfigErrors(errors);
    return !errors.length;
  };

  const onDeployButtonClick = async () => {
      try {
        setPipeState(PipelineState.STARTING);
        await deployStream({id: props.topology.id, env: props.topology.environnement}).unwrap();
        setStartingTimeout();
      } catch (error: any) {
        const errorMessage =
          error?.data?.message && error.data.message.length <= 256
            ? error.data.message
            : "Echec du lancement";

        setPipeState(PipelineState.DELETED);

        dispatch(setToast({
          severity: "error",
          content: errorMessage,
          life: 5000
        }));
    }
  };

  const onStopButtonClick = async () => {
      // stop timer
      cancelStartingTimeout();
      try {
        // Indicate deleting is in progress
        setPipeState(PipelineState.DELETING);
        const response = await deleteStream({
          name: props.topology.id,
          env: props.topology.environnement,
        }).unwrap();
        if (response === "" || response == null) {
          setPipeState(PipelineState.RUNNING);
          dispatch(setToast({
            severity: "error",
            content: "Aucun flux en cours, réessayez",
            life: 5000,
          }));
        }
        // Synchronize the topology
        getTopology({id: props.topology.id, version: props.topology.version});
      } catch {
        // In case of failure
        setPipeState(PipelineState.RUNNING);
        dispatch(setToast({severity: "error", content: "Echec de l'arrêt", life: 5000}));
    }
  };

  const onPauseButtonClick = async () => {
      try {
        // Indicate deleting is in progress
        setPipeState(PipelineState.STOPPING);
        await stopStream({name: props.topology.id, env: props.topology.environnement}).unwrap();
      } catch {
        // In case of failure
        setPipeState(PipelineState.RUNNING);
        dispatch(setToast({severity: "error", content: "Echec du stop", life: 5000}));
    }
  }

  const onResumeButtonClick = async () => {
    try {
      setPipeState(PipelineState.STARTING);
    await resumeStream({id: props.topology.id, env: props.topology.environnement}).unwrap();
      setStartingTimeout();
    } catch {
      setPipeState(PipelineState.STOPPED);
      dispatch(setToast({severity: "error", content: "Echec du redémarrage", life: 5000}));
    }
  }

  const showRetentionTimeWarning = () => {
    confirmDialog({
      header: 'Rétention des données',
      message: (
        <div>
          Le temps de rétention des données est fixé à <b>{props.topology?.retentionTimeNats}</b>.<br/>
          Au delà de ce délai, les données en cours de traitement seront perdues.
        </div>
      ),
      icon: 'pi pi-info-circle',
      acceptLabel: 'Ok',
      rejectLabel: 'Annuler',
      accept: () => onPauseButtonClick()
    });
  }

  if (!hasAnyPerm("flows.all", "flows_startstop")) {
    return null;
  }


// -------------------------------------------------------------------------------------------------
// PRIVATE HTML
// -------------------------------------------------------------------------------------------------

  const getFormattedConfigErrors = () => {
    return (
      <DataTable
        value={configErrors}
        stripedRows
        rowsPerPageOptions={[10, 25, 50]}
        sortField="id"
        sortOrder={1}
      >
        <Column field="id" header="Brique" className={"w-3"} sortable></Column>
        <Column field="error" header="Erreur" className={"w-6"}></Column>
      </DataTable>
    );
  };

// -------------------------------------------------------------------------------------------------
// SUB COMPONENTS
// -------------------------------------------------------------------------------------------------

  const StopButton = (
    <Button rounded
      label="Stopper"
      icon={`pi pi-stop`}
      onClick={onStopButtonClick}
    />
  );

  const PauseButton = (
    <Button rounded
      icon={`pi pi-pause`}
      onClick={() => {
        const isANALYTIC = props.topology.runtimeMode === RuntimeMode.ANALYTIC;
        if (!isANALYTIC && props.topology?.retentionTimeNats && props.topology?.retentionTimeNats !== "") {
          showRetentionTimeWarning();
        } else {
          onPauseButtonClick();
        }
      }}
    />
  );

  const DeployButton = (
    <Button rounded
      data-pr-tooltip={t("button.savebeforelaunch")}
      label={forcedFlowName ? t("button.advancedlaunch") : t("button.launch")}
      disabled={pipelineSaved || debugMode}
      icon={`pi ${deployStatus.isLoading ? "pi-spin pi-spinner" : "pi-caret-right"}`}
      onClick={() => {
        if (verifyTopology()) {
          onDeployButtonClick();
        }
      }}
      className={"deploy-pipeline"}
    ></Button>
  );

  const ResumeButton = (
    <Button rounded
      label={'Reprendre'}
      icon="pi pi-caret-right"
      onClick={() => {
        if (verifyTopology()) {
          onResumeButtonClick();
        }
      }}
    ></Button>
  );

  const LoadingButton = (label: string) => {
    return (
      <Button rounded text
        label={label}
        icon={`pi pi-spin pi-spinner`}
        disabled={true}
      />
    );
  };

  const isPauseAllowed = (): boolean => {
    const isCRON = props.topology.schedule !== undefined && props.topology.schedule != "";
    const isANALYTIC = props.topology.runtimeMode === RuntimeMode.ANALYTIC;
    return !isANALYTIC && !isCRON;
  }

// -------------------------------------------------------------------------------------------------
// MAIN RENDERER
// -------------------------------------------------------------------------------------------------

  return (
    <>
      {!props.onlyStop && pipeState === PipelineState.DELETED && DeployButton}
      {!props.onlyStop && pipeState === PipelineState.COMPLETED && DeployButton}
      {pipeState === PipelineState.STOPPED && ResumeButton}
      {pipeState === PipelineState.RUNNING && StopButton}
      {pipeState === PipelineState.FAILED && DeployButton}
      {pipeState === PipelineState.RUNNING && isPauseAllowed() && PauseButton}
      {pipeState === PipelineState.UNDEFINED && LoadingButton('Récupération du statut...')}
      {pipeState === PipelineState.STARTING && LoadingButton('Démarrage...')}
      {pipeState === PipelineState.STOPPING && LoadingButton('Mise en pause...')}
      {pipeState === PipelineState.DELETING && LoadingButton('Arrêt en cours...')}
      <Dialog header={t("error.stream.launch")} visible={!!configErrors.length} style={{ width: "35vw" }} onHide={() => setConfigErrors([])}>
        <p className="m-0">{getFormattedConfigErrors()}</p>
      </Dialog>
      <ConfirmDialog />
    </>
  );
};